import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FormGroup, FormBuilder, Validators, AbstractControl, ValidationErrors, FormControl } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { SevaProfileService } from 'src/app/Service/seva-profile.service';
import { CONSTANTS, INDIAN_STATES, RELATIONSHIP_OPTIONS } from 'src/Core/Constant';
import { GenericValidationsService } from 'src/app/Service/generic-validations.service';
import { TranslationService } from 'src/app/Service/translation.service';
import { COUNTRY_DIAL_CODES } from 'src/Core/CountryCodes';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-address-section',
  templateUrl: './address-section.component.html',
  styleUrls: ['./address-section.component.scss']
})
export class AddressSectionComponent {

  @Output() nextStep = new EventEmitter<void>();
  @Output() previous = new EventEmitter<void>();
  @Input() applicantData: any;
  @Input() isPreviewMode = false;
  CONSTANTS = CONSTANTS;
  addressForm!: FormGroup;
  submitted = false;

  SaveMode = CONSTANTS.SAVE_MODE;
  indianStateOptions = INDIAN_STATES;
  relationshipOptions = RELATIONSHIP_OPTIONS;
  countryCodeOptions = COUNTRY_DIAL_CODES;
  isJsonShow = environment.isJsonShow;
  isEditable: boolean = true; // Flag to track if the form is editable

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private api: SevaProfileService,
    private translation: TranslationService,
    public genericValidations: GenericValidationsService
  ) {
    this.buildForm();
  }

  // ================= GETTERS =================

  get currentAddressForm(): FormGroup { 
    return this.addressForm.get('currentAddress') as FormGroup; 
  }

  get permanentAddressForm(): FormGroup { 
    return this.addressForm.get('permanentAddress') as FormGroup; 
  }
  
  get emergencyContactForm(): FormGroup { 
    return this.addressForm.get('emergencyContact') as FormGroup; 
  }
  
  get sameAsCurrentAddressControl(): FormControl { 
    return this.addressForm.get('SameAsCurrrentAddress') as FormControl; 
  }

  // ================= INIT =================
  ngOnInit(): void {
    if (!this.isPreviewMode) {
      this.getApplicantData();
    }
  
    // 🔥 AUTO SYNC current -> permanent when SameAs is true
    this.currentAddressForm.valueChanges.subscribe(currentValue => {
      if (this.sameAsCurrentAddressControl.value) {
        const normalizedValue = this.ensureDefaultCountryCode(currentValue);
        // Preserve AddressType as Permanent (2) when syncing
        normalizedValue.AddressType = CONSTANTS.AddressType.Permanant;
        this.permanentAddressForm.patchValue(normalizedValue, { emitEvent: false });
      }
    });
  
 

    
    // 🔥 React to checkbox changes (clean Angular way)
    this.sameAsCurrentAddressControl.valueChanges.subscribe(checked => {
      this.applySameAsCurrentAddress(checked);
    });

  
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['applicantData'] && this.applicantData && this.isPreviewMode) {
      this.patchFormWithApplicantData(this.applicantData);
      this.addressForm.disable();
    }
  }
  // ================= FORM BUILD =================
  private buildForm(): void {

    this.addressForm = this.fb.group({
      currentAddress: this.createAddressGroup(CONSTANTS.AddressType.Current),
      SameAsCurrrentAddress: [false],
      SaveMode: [''],
      permanentAddress: this.createAddressGroup(CONSTANTS.AddressType.Permanant),
      emergencyContact: this.createEmergencyGroup()
    });
    
    // Add custom validator after form is built
    const emergencyGroup = this.addressForm.get('emergencyContact') as FormGroup;
    emergencyGroup.get('Mobile')?.setValidators([
      ...this.getMobileValidators('+91'),
      this.disallowSameAsCurrentMobile()
    ]);
  }

  private createAddressGroup(addressType: number): FormGroup {
    return this.fb.group({
      StreetAddress: ['', Validators.required],
      TownCity: ['', Validators.required],
      District: ['', Validators.required],
      State: ['', Validators.required],
      PinCode: ['', [Validators.required, Validators.pattern(/^[0-9]{6}$/)]],
      CountryCode: ['+91', Validators.required],
      Mobile: ['', this.getMobileValidators('+91')],
      WorkPhone: [''],
      Email: [''],
      AddressType: [addressType],
      SaveMode: ['']
    });
  }

  private createEmergencyGroup(): FormGroup {
    return this.fb.group({
      EMERGENCYContactName: ['', Validators.required],
      EMERGENCYContacRelationship: ['', Validators.required],
      CountryCode: ['+91', Validators.required],
      Mobile: ['', this.getMobileValidators('+91')],
      WorkPhone: [''],
      Email: ['', Validators.email],
      AddressType: [CONSTANTS.AddressType.Emergency],
      SaveMode: ['']
    });
  }

  // ================= MOBILE VALIDATORS =================
  private getMobileValidators(code: string) {
    return code === '+91'
      ? [Validators.required, Validators.pattern(/^[7-9][0-9]{9}$/), Validators.maxLength(10)]
      : [Validators.required, Validators.pattern(/^[0-9]{8,15}$/), Validators.maxLength(15)];
  }

  onCountryCodeChange(section: 'current' | 'permanent' | 'emergency', code: string) {
    const form = this.getSectionForm(section);
    const validators = this.getMobileValidators(code);

    if (section === 'emergency') {
      validators.push(this.disallowSameAsCurrentMobile());
    }

    form.get('Mobile')?.setValidators(validators);
    form.get('Mobile')?.updateValueAndValidity();
  }

  private getSectionForm(section: string): FormGroup {
    return section === 'current'
      ? this.currentAddressForm
      : section === 'permanent'
      ? this.permanentAddressForm
      : this.emergencyContactForm;
  }

  // ================= SAME AS CURRENT =================
  onSameAsResidentialAddressChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.addressForm.patchValue({ SameAsCurrrentAddress: checked });

    if (checked) {
      const currentValue = this.ensureDefaultCountryCode(this.currentAddressForm.value);
      // Preserve AddressType as Permanent (2) when syncing
      currentValue.AddressType = CONSTANTS.AddressType.Permanant;
      this.permanentAddressForm.patchValue(currentValue);
      this.permanentAddressForm.disable();
    } else {
      this.permanentAddressForm.enable();
      this.permanentAddressForm.reset({ CountryCode: '+91', AddressType: CONSTANTS.AddressType.Permanant });
    }
  }

  // ================= CUSTOM VALIDATOR =================
  private disallowSameAsCurrentMobile() {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.addressForm) {
        return null;
      }
      const currentAddressGroup = this.addressForm.get('currentAddress') as FormGroup;
      if (!currentAddressGroup) {
        return null;
      }
      const current = currentAddressGroup.get('Mobile')?.value;
      const emergency = control.value;
      return current && emergency && current === emergency
        ? { sameAsCurrentMobile: true }
        : null;
    };
  }



  private logAllInvalidControls(form: FormGroup, parentPath: string = ''): void {
    Object.keys(form.controls).forEach(key => {
      const control = form.get(key);
  
      const controlPath = parentPath ? `${parentPath}.${key}` : key;
  
      if (control instanceof FormGroup) {
        this.logAllInvalidControls(control, controlPath);
      } else {
        if (control && control.invalid) {
          console.log('❌ Invalid Field:', controlPath);
          console.log('   Value:', control.value);
          console.log('   Errors:', control.errors);
        }
      }
    });
  }
  


  // ================= SAVE =================
  goToNext(mode: number) {
    
    if (mode === this.SaveMode.SAVE && this.addressForm.invalid) {
      this.submitted = true;
      this.toastr.warning(this.getTranslation('validation.completeAllFields'));
      this.addressForm.markAllAsTouched();
        // 🔥 PRINT ALL INVALID FIELDS
    this.logAllInvalidControls(this.addressForm);
      return;

    }

    const payload = this.buildPayload(mode);
    this.saveAllAddresses(payload, mode);
  }

  private buildPayload(mode: number) {
    this.currentAddressForm.patchValue({ SaveMode: mode });
    this.permanentAddressForm.patchValue({ SaveMode: mode });
    this.emergencyContactForm.patchValue({ SaveMode: mode });

    const currentAddress = this.ensureDefaultCountryCode(this.currentAddressForm.getRawValue());
    const permanentAddress = this.ensureDefaultCountryCode(this.permanentAddressForm.getRawValue());
    const emergencyAddress = this.ensureDefaultCountryCode(this.emergencyContactForm.getRawValue());

    // Ensure AddressType is correctly set for each address
    currentAddress.AddressType = CONSTANTS.AddressType.Current;
    permanentAddress.AddressType = CONSTANTS.AddressType.Permanant;
    emergencyAddress.AddressType = CONSTANTS.AddressType.Emergency;

    return {
      SameAsCurrrentAddress: this.sameAsCurrentAddressControl.value?.toString(),
      CurrentAddress: currentAddress,
      PermanentAddress: permanentAddress,
      EmergencyAddress: emergencyAddress,
      SaveMode: mode,
      // IsEditable: true
    };
  }

  private saveAllAddresses(payload: any, mode: number) {
    this.api.apiCall('Address/SaveAllAddresses', 'POST', payload).subscribe(() => {
      this.toastr.success(this.getTranslation('success.informationSaved'));
      mode === this.SaveMode.SAVE ? this.nextStep.emit() : this.getApplicantData();
    });
  }



  // ================= LOAD DATA =================
  private getApplicantData() {
    this.api.apiCall('Address/GetAllAddress', 'GET').subscribe((res: any) => {
      if (!res?.Data) return;
      this.genericValidations.setProgressPercentage(res.Data.CompletionPercentage); // Example: Set progress to 50%

      // Store IsEditable flag from response
      this.isEditable = res.Data.IsEditable !== false; // Default to true if not provided
      this.addressForm.patchValue({SaveMode: res.Data.SaveMode});
      
      // Disable form if not editable
      if (!this.isEditable) {
        this.addressForm.disable();
      } else {
        this.addressForm.enable();
      }

      const currentAddress = this.ensureDefaultCountryCode(res.Data.CurrentAddress || {});
      const permanentAddress = this.ensureDefaultCountryCode(res.Data.PermanentAddress || {});
      const emergencyAddress = this.ensureDefaultCountryCode(res.Data.EmergencyAddress || {});
      
      // Ensure AddressType is set correctly from API or use defaults
      if (!currentAddress.AddressType) currentAddress.AddressType = CONSTANTS.AddressType.Current;
      if (!permanentAddress.AddressType) permanentAddress.AddressType = CONSTANTS.AddressType.Permanant;
      if (!emergencyAddress.AddressType) emergencyAddress.AddressType = CONSTANTS.AddressType.Emergency;
      
      this.currentAddressForm.patchValue(currentAddress);
      this.permanentAddressForm.patchValue(permanentAddress);
      this.emergencyContactForm.patchValue(emergencyAddress);
      
      // Convert string boolean to actual boolean
      const sameAsCurrent = this.convertToBoolean(res.Data.SameAsCurrrentAddress);
      this.addressForm.patchValue({ SameAsCurrrentAddress: sameAsCurrent });
      
      // Ensure all CountryCode fields are normalized
      this.normalizeAllCountryCodes();
     
      if (this.isPreviewMode) {
        this.addressForm.disable();
      }
    });
  }

  patchFormWithApplicantData(data: any): void {
    if (data) {
      // Extract address data from applicantData
      // The data structure may have CurrentAddress, PermanentAddress, EmergencyAddress directly
      // or nested in a different structure
      const currentAddress = this.ensureDefaultCountryCode(data.CurrentAddress || {});
      const permanentAddress = this.ensureDefaultCountryCode(data.PermanentAddress || {});
      const emergencyAddress = this.ensureDefaultCountryCode(data.EmergencyAddress || {});

      // Ensure AddressType is set correctly from API or use defaults
      if (!currentAddress.AddressType) currentAddress.AddressType = CONSTANTS.AddressType.Current;
      if (!permanentAddress.AddressType) permanentAddress.AddressType = CONSTANTS.AddressType.Permanant;
      if (!emergencyAddress.AddressType) emergencyAddress.AddressType = CONSTANTS.AddressType.Emergency;

      this.currentAddressForm.patchValue(currentAddress);
      this.permanentAddressForm.patchValue(permanentAddress);
      this.emergencyContactForm.patchValue(emergencyAddress);
      
      // Convert string boolean to actual boolean
      const sameAsCurrent = this.convertToBoolean(data.SameAsCurrrentAddress);
      this.addressForm.patchValue({ SameAsCurrrentAddress: sameAsCurrent });
      
      // Ensure all CountryCode fields are normalized
      this.normalizeAllCountryCodes();
    }
  }


  applySameAsCurrentAddress(checked: boolean): void {
    if (checked) {
      const currentValue = this.ensureDefaultCountryCode(this.currentAddressForm.value);
      // Preserve AddressType as Permanent (2) when syncing
      currentValue.AddressType = CONSTANTS.AddressType.Permanant;
      this.permanentAddressForm.patchValue(currentValue);
      this.permanentAddressForm.disable();
    } else {
      this.permanentAddressForm.enable();
      this.permanentAddressForm.patchValue({AddressType: CONSTANTS.AddressType.Permanant});
      this.permanentAddressForm.reset({ CountryCode: '+91', AddressType: CONSTANTS.AddressType.Permanant });
    }
  }

  // ================= UTILS =================
  private ensureDefaultCountryCode(data: any): any {
    if (!data) return data;
    return {
      ...data,
      CountryCode: data.CountryCode && data.CountryCode.trim() !== '' ? data.CountryCode : '+91'
    };
  }

  private convertToBoolean(value: any): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      const lowerValue = value.toLowerCase().trim();
      return lowerValue === 'true' || lowerValue === '1';
    }
    return Boolean(value);
  }

  private normalizeAllCountryCodes(): void {
    // Ensure all CountryCode fields default to '+91' if null or blank
    [this.currentAddressForm, this.permanentAddressForm, this.emergencyContactForm].forEach(form => {
      const countryCodeControl = form.get('CountryCode');
      if (countryCodeControl && (!countryCodeControl.value || countryCodeControl.value.trim() === '')) {
        countryCodeControl.setValue('+91', { emitEvent: false });
      }
    });
  }

  goToBack() { this.previous.emit(); }
  getTranslation(key: string) { return this.translation.getTranslation(key); }
}
